import { create } from 'zustand';
import { getSiteAccess } from '@/api/access';
import { ApiError } from '@/api/client';
import type { AccessStatus, SiteAccess } from '@/types/access';

interface AccessState {
  siteId: string | null;
  access: SiteAccess | null;
  status: AccessStatus;
  error: string | null;
  generation: number;
  revision: number;
  load: (siteId: string, refresh?: boolean) => Promise<void>;
  reset: () => void;
  can: (permission: string) => boolean;
}

export const useSiteAccessStore = create<AccessState>((set, get) => ({
  siteId: null, access: null, status: 'idle', error: null, generation: 0, revision: 0,
  reset: () => set((s) => ({ siteId: null, access: null, status: 'idle', error: null,
    generation: s.generation + 1, revision: s.revision + 1 })),
  can: (permission) => {
    const { access, status } = get();
    return status === 'ready' && !!access?.allowed && access.site_active &&
      (!access.expires_at || Date.parse(access.expires_at) > Date.now()) &&
      access.permissions.includes(permission);
  },
  load: async (siteId, refresh = false) => {
    const prior = get();
    const generation = prior.generation + 1;
    const preserving = refresh && prior.siteId === siteId && prior.status === 'ready';
    set({ siteId, generation, error: null,
      ...(preserving ? {} : { access: null, status: 'loading' as const }) });
    try {
      const access = await getSiteAccess(siteId);
      if (get().generation !== generation || get().siteId !== siteId) return;
      if (access.site_id !== siteId) throw new Error('현장 권한 응답이 일치하지 않습니다.');
      if (!Array.isArray(access.permissions) || access.permissions.some((p) => typeof p !== 'string') ||
        (access.role_version !== null && typeof access.role_version !== 'string')) {
        throw new Error('현장 권한 응답 형식이 올바르지 않습니다.');
      }
      const allowed = access.allowed && access.site_active &&
        (!access.expires_at || Date.parse(access.expires_at) > Date.now());
      const changed = JSON.stringify(prior.access) !== JSON.stringify(access);
      set({ access: allowed ? access : null, status: allowed ? 'ready' : 'denied',
        revision: changed ? get().revision + 1 : get().revision });
    } catch (error) {
      if (get().generation !== generation || get().siteId !== siteId) return;
      const denied = error instanceof ApiError && (error.status === 403 || error.status === 404);
      set({ access: null, status: denied ? 'denied' : 'error',
        error: denied ? '이 현장의 CCTV 이용 권한이 없습니다.' :
          error instanceof Error ? error.message : '현장 권한을 확인하지 못했습니다.',
        revision: get().revision + 1 });
    }
  },
}));
